import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, X, FileText, Loader2, Eye, Paperclip } from "lucide-react";
import { toast } from "sonner";

export default function FileUploader({ value = [], onChange }) {
  const fileInput = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const uploaded = [...value];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data);
      } catch (err) {
        toast.error(`Failed: ${f.name}`);
      }
    }
    setUploading(false);
    onChange(uploaded);
    if (fileInput.current) fileInput.current.value = "";
  };

  const removeFile = (idx) => {
    const next = [...value];
    next.splice(idx, 1);
    onChange(next);
  };

  const downloadUrl = (id) => {
    // Cookie-based auth (same-origin /api) — no JWT in the URL.
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/${id}`;
  };

  return (
    <div>
      <input
        ref={fileInput}
        type="file"
        multiple
        onChange={handleUpload}
        className="hidden"
        data-testid="file-input"
      />
      <Button
        type="button"
        variant="outline"
        onClick={() => fileInput.current?.click()}
        disabled={uploading}
        className="w-full justify-start"
        data-testid="file-upload-button"
      >
        {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
        {uploading ? "Uploading..." : "Click to upload files"}
      </Button>
      {value.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {value.map((f, i) => (
            <div key={f.id || `${f.filename || "file"}-${i}`} className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-xs">
              <Paperclip className="h-3.5 w-3.5 text-slate-500 flex-shrink-0" />
              <span className="flex-1 truncate text-slate-700">{f.filename || f.original_filename || f.id}</span>
              <a
                href={downloadUrl(f.id)}
                target="_blank"
                rel="noreferrer"
                className="p-1 hover:bg-slate-200 rounded"
                data-testid={`file-preview-${i}`}
              >
                <Eye className="h-3.5 w-3.5 text-slate-600" />
              </a>
              <button
                type="button"
                onClick={() => removeFile(i)}
                className="p-1 hover:bg-slate-200 rounded"
                data-testid={`file-remove-${i}`}
              >
                <X className="h-3.5 w-3.5 text-slate-600" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
